import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { BrandingProvider } from './contexts/BrandingContext';
import ErrorBoundary from './components/ErrorBoundary';
import { PrivateRoute } from './components/PrivateRoute';
import { PWAUpdatePrompt } from './components/PWAUpdatePrompt';
import RefreshResultToast from './components/RefreshResultToast';
import { LoadingSpinner } from './components/ui';

// Eager loaded - frequently used pages
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import PasswordResetPage from './pages/PasswordResetPage';
import AuthActionPage from './pages/AuthActionPage';
import DashboardPage from './pages/DashboardPage';
import AssignmentsPage from './pages/AssignmentsPage';
import Layout from './components/Layout';

// Lazy loaded - less frequently used parent pages
const ChildrenPage = lazy(() => import('./pages/ChildrenPage'));
const ChildDetailPage = lazy(() => import('./pages/ChildDetailPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const FeesPage = lazy(() => import('./pages/FeesPage'));
const VolunteerPage = lazy(() => import('./pages/VolunteerPage'));
const ProfileCompletePage = lazy(() => import('./pages/ProfileCompletePage'));

// Lazy loaded - public enrollment page
const EnrollmentPage = lazy(() => import('./pages/EnrollmentPage'));

// Lazy loaded - admin pages (only loaded when admin accesses them)
const AdminLayout = lazy(() => import('./components/AdminLayout'));
const AdminPage = lazy(() => import('./pages/admin/AdminPage'));
const AdminFamiliesPage = lazy(() => import('./pages/admin/AdminFamiliesPage'));
const AdminInvitationsPage = lazy(() => import('./pages/admin/AdminInvitationsPage'));
const EmailTemplatesPage = lazy(() => import('./pages/admin/EmailTemplatesPage'));
const DeclarationsPage = lazy(() => import('./pages/admin/DeclarationsPage'));
const DeclarationMatrixPage = lazy(() => import('./pages/admin/DeclarationMatrixPage'));
const ReportsPage = lazy(() => import('./pages/admin/ReportsPage'));
const BrandingSettingsPage = lazy(() => import('./pages/admin/BrandingSettingsPage'));
const FeeManagementPage = lazy(() => import('./pages/admin/FeeManagementPage'));
const FeeSettingsPage = lazy(() => import('./pages/admin/FeeSettingsPage'));
const AdminPayrollSettingsPage = lazy(() => import('./pages/admin/AdminPayrollSettingsPage'));
const AdminPayrollDashboardPage = lazy(() => import('./pages/admin/AdminPayrollDashboardPage'));
const AdminPayrollPlanningPage = lazy(() => import('./pages/admin/AdminPayrollPlanningPage'));
const AdminPayrollPreparePage = lazy(() => import('./pages/admin/AdminPayrollPreparePage'));
const AdminPayrollTeacherDetailPage = lazy(() => import('./pages/admin/AdminPayrollTeacherDetailPage'));
const AdminPayrollLohnzettelPage = lazy(() => import('./pages/admin/AdminPayrollLohnzettelPage'));
const AdminHoursApprovalsPage = lazy(() => import('./pages/admin/AdminHoursApprovalsPage'));
const AdminEnrollmentsPage = lazy(() => import('./pages/admin/AdminEnrollmentsPage'));
const SchoolSettingsPage = lazy(() => import('./pages/admin/SchoolSettingsPage'));
const EnrollmentFormSettingsPage = lazy(() => import('./pages/admin/EnrollmentFormSettingsPage'));
const AuditLogPage = lazy(() => import('./pages/admin/AuditLogPage'));
const ImportConfigPage = lazy(() => import('./pages/admin/ImportConfigPage'));
const VolunteersPage = lazy(() => import('./pages/admin/VolunteersPage'));
const AdminTeachersPage = lazy(() => import('./pages/admin/AdminTeachersPage'));
const AdminTeacherProfilePage = lazy(() => import('./pages/admin/AdminTeacherProfilePage'));
const AdminClassesPage = lazy(() => import('./pages/admin/AdminClassesPage'));
const AdminScheduleSlotsPage = lazy(() => import('./pages/admin/AdminScheduleSlotsPage'));
const AdminRoomsPage = lazy(() => import('./pages/admin/AdminRoomsPage'));
const AdminCapacityDashboardPage = lazy(() => import('./pages/admin/AdminCapacityDashboardPage'));
const AdminHolidaysPage = lazy(() => import('./pages/admin/AdminHolidaysPage'));
const AdminCategoriesPage = lazy(() => import('./pages/admin/AdminCategoriesPage'));
const AdminDataIntegrityPage = lazy(() => import('./pages/admin/AdminDataIntegrityPage'));
// Phase 5.5 — Schedule View umbrella. /admin/schedule е dashboard (widgets),
// sub-routes са табове: /slots (бившата AdminScheduleSlotsPage), /rooms,
// /teachers, /classes. Production deploy изчаква Phase 5A promote.
const AdminSchedulePage = lazy(() => import('./pages/admin/AdminSchedulePage'));
const AdminScheduleDashboardPage = lazy(() => import('./pages/admin/AdminScheduleDashboardPage'));
const AdminScheduleRoomsPage = lazy(() => import('./pages/admin/AdminScheduleRoomsPage'));
const AdminScheduleTeachersPage = lazy(() => import('./pages/admin/AdminScheduleTeachersPage'));
const AdminScheduleClassesPage = lazy(() => import('./pages/admin/AdminScheduleClassesPage'));
const AdminScheduleRearrangePage = lazy(() => import('./pages/admin/AdminScheduleRearrangePage'));
const AssignDeclarationsSettingsPage = lazy(() => import('./pages/admin/AssignDeclarationsSettingsPage'));
const AdminManagementPage = lazy(() => import('./pages/admin/AdminManagementPage'));
const SystemSettingsPage = lazy(() => import('./pages/admin/SystemSettingsPage'));
const GdprAuditLogPage = lazy(() => import('./pages/admin/GdprAuditLogPage'));
const EmergencyAccessLogPage = lazy(() => import('./pages/admin/EmergencyAccessLogPage'));
const ExpenseManagementPage = lazy(() => import('./pages/admin/ExpenseManagementPage'));
const FaqPage = lazy(() => import('./pages/FaqPage'));

// Lazy loaded - teacher pages
const TeacherLayout = lazy(() => import('./components/TeacherLayout'));
const TeacherDashboardPage = lazy(() => import('./pages/teacher/TeacherDashboardPage'));
const ClassViewPage = lazy(() => import('./pages/teacher/ClassViewPage'));
const EmergencyAccessPage = lazy(() => import('./pages/teacher/EmergencyAccessPage'));
const TeacherExpensesPage = lazy(() => import('./pages/teacher/TeacherExpensesPage'));
const SubmitExpensePage = lazy(() => import('./pages/teacher/SubmitExpensePage'));
const TeacherAdvanceAccountabilityPage = lazy(() => import('./pages/teacher/TeacherAdvanceAccountabilityPage'));
const TeacherDeclareHoursPage = lazy(() => import('./pages/teacher/TeacherDeclareHoursPage'));
const TeacherMyHoursPage = lazy(() => import('./pages/teacher/TeacherMyHoursPage'));
const TeacherSalaryPage = lazy(() => import('./pages/teacher/TeacherSalaryPage'));

function PageLoader() {
  return <LoadingSpinner />;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, customClaims, loading } = useAuth();

  if (loading) return <LoadingSpinner />;

  if (!user) {
    return <Navigate to="/login" />;
  }

  // Check admin role (supports both role and roles fields, superadmin inherits admin)
  const hasAdminRole = customClaims?.role === 'admin' ||
    customClaims?.role === 'superadmin' ||
    (customClaims?.roles && (customClaims.roles.includes('admin') || customClaims.roles.includes('superadmin')));

  if (!hasAdminRole) {
    return <Navigate to="/" />;
  }

  return <>{children}</>;
}

function TeacherRoute({ children }: { children: React.ReactNode }) {
  const { user, customClaims, loading } = useAuth();

  if (loading) return <LoadingSpinner />;

  if (!user) {
    return <Navigate to="/login" />;
  }

  // Check if user has teacher role in their roles array
  const hasTeacherRole = customClaims?.roles?.includes('teacher') || customClaims?.role === 'teacher';

  if (!hasTeacherRole) {
    return <Navigate to="/" />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  const isEnrollmentDomain = window.location.hostname.startsWith('zapisvane.');

  // On zapisvane.* domain, show only the enrollment page
  if (isEnrollmentDomain) {
    return (
      <Routes>
        <Route path="*" element={
          <BrandingProvider>
            <Suspense fallback={<PageLoader />}>
              <EnrollmentPage />
            </Suspense>
          </BrandingProvider>
        } />
      </Routes>
    );
  }

  return (
    <Routes>
      {/* Public Routes — wrapped in BrandingProvider for logo/colors */}
      <Route path="/login" element={<BrandingProvider><LoginPage /></BrandingProvider>} />
      <Route path="/signup" element={<BrandingProvider><SignupPage /></BrandingProvider>} />
      <Route path="/reset-password" element={<BrandingProvider><PasswordResetPage /></BrandingProvider>} />
      <Route path="/auth/action" element={<BrandingProvider><AuthActionPage /></BrandingProvider>} />
      <Route path="/enroll" element={
        <BrandingProvider>
          <Suspense fallback={<PageLoader />}>
            <EnrollmentPage />
          </Suspense>
        </BrandingProvider>
      } />
      <Route path="/enroll-complete" element={
        <BrandingProvider>
          <Suspense fallback={<PageLoader />}>
            <EnrollmentPage mode="invitation" />
          </Suspense>
        </BrandingProvider>
      } />

      {/* Parent Routes */}
      <Route
        path="/"
        element={
          <PrivateRoute>
            <BrandingProvider>
              <Layout />
            </BrandingProvider>
          </PrivateRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="assignments" element={<AssignmentsPage />} />
        <Route path="children" element={
          <Suspense fallback={<PageLoader />}>
            <ChildrenPage />
          </Suspense>
        } />
        <Route path="children/:childId" element={
          <Suspense fallback={<PageLoader />}>
            <ChildDetailPage />
          </Suspense>
        } />
        <Route path="profile" element={
          <Suspense fallback={<PageLoader />}>
            <ProfilePage />
          </Suspense>
        } />
        <Route path="profile/complete" element={
          <Suspense fallback={<PageLoader />}>
            <ProfileCompletePage />
          </Suspense>
        } />
        <Route path="fees" element={
          <Suspense fallback={<PageLoader />}>
            <FeesPage />
          </Suspense>
        } />
        <Route path="volunteer" element={
          <Suspense fallback={<PageLoader />}>
            <VolunteerPage />
          </Suspense>
        } />
        <Route path="faq" element={
          <Suspense fallback={<PageLoader />}>
            <FaqPage />
          </Suspense>
        } />
      </Route>

      {/* Admin Routes - all lazy loaded */}
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <BrandingProvider>
              <Suspense fallback={<PageLoader />}>
                <AdminLayout />
              </Suspense>
            </BrandingProvider>
          </AdminRoute>
        }
      >
        <Route index element={
          <Suspense fallback={<PageLoader />}>
            <AdminPage />
          </Suspense>
        } />
        <Route path="families" element={
          <Suspense fallback={<PageLoader />}>
            <AdminFamiliesPage />
          </Suspense>
        } />
        <Route path="invitations" element={
          <Suspense fallback={<PageLoader />}>
            <AdminInvitationsPage />
          </Suspense>
        } />
        <Route path="declarations" element={
          <Suspense fallback={<PageLoader />}>
            <DeclarationsPage />
          </Suspense>
        } />
        <Route path="declarations/matrix" element={
          <Suspense fallback={<PageLoader />}>
            <DeclarationMatrixPage />
          </Suspense>
        } />
        <Route path="declarations/assign" element={
          <Suspense fallback={<PageLoader />}>
            <AssignDeclarationsSettingsPage />
          </Suspense>
        } />
        <Route path="email-templates" element={
          <Suspense fallback={<PageLoader />}>
            <EmailTemplatesPage />
          </Suspense>
        } />
        <Route path="reports" element={
          <Suspense fallback={<PageLoader />}>
            <ReportsPage />
          </Suspense>
        } />
        <Route path="settings" element={
          <Suspense fallback={<PageLoader />}>
            <SchoolSettingsPage />
          </Suspense>
        } />
        <Route path="settings/branding" element={
          <Suspense fallback={<PageLoader />}>
            <BrandingSettingsPage />
          </Suspense>
        } />
        <Route path="fees" element={
          <Suspense fallback={<PageLoader />}>
            <FeeManagementPage />
          </Suspense>
        } />
        <Route path="expenses" element={
          <Suspense fallback={<PageLoader />}>
            <ExpenseManagementPage />
          </Suspense>
        } />
        <Route path="fees/settings" element={
          <Suspense fallback={<PageLoader />}>
            <FeeSettingsPage />
          </Suspense>
        } />
        <Route path="payroll" element={
          <Suspense fallback={<PageLoader />}>
            <AdminPayrollDashboardPage />
          </Suspense>
        } />
        <Route path="payroll/teacher/:teacherId" element={
          <Suspense fallback={<PageLoader />}>
            <AdminPayrollTeacherDetailPage />
          </Suspense>
        } />
        <Route path="payroll/lohnzettel" element={
          <Suspense fallback={<PageLoader />}>
            <AdminPayrollLohnzettelPage />
          </Suspense>
        } />
        <Route path="payroll/planning" element={
          <Suspense fallback={<PageLoader />}>
            <AdminPayrollPlanningPage />
          </Suspense>
        } />
        <Route path="payroll/prepare" element={
          <Suspense fallback={<PageLoader />}>
            <AdminPayrollPreparePage />
          </Suspense>
        } />
        <Route path="hours" element={
          <Suspense fallback={<PageLoader />}>
            <AdminHoursApprovalsPage />
          </Suspense>
        } />
        <Route path="payroll/settings" element={
          <Suspense fallback={<PageLoader />}>
            <AdminPayrollSettingsPage />
          </Suspense>
        } />
        <Route path="enrollments" element={
          <Suspense fallback={<PageLoader />}>
            <AdminEnrollmentsPage />
          </Suspense>
        } />
        <Route path="enrollment-form" element={
          <Suspense fallback={<PageLoader />}>
            <EnrollmentFormSettingsPage />
          </Suspense>
        } />
        <Route path="volunteers" element={
          <Suspense fallback={<PageLoader />}>
            <VolunteersPage />
          </Suspense>
        } />
        <Route path="audit-log" element={
          <Suspense fallback={<PageLoader />}>
            <AuditLogPage />
          </Suspense>
        } />
        <Route path="import-config" element={
          <Suspense fallback={<PageLoader />}>
            <ImportConfigPage />
          </Suspense>
        } />
        <Route path="teachers" element={
          <Suspense fallback={<PageLoader />}>
            <AdminTeachersPage />
          </Suspense>
        } />
        <Route path="teachers/:teacherId" element={
          <Suspense fallback={<PageLoader />}>
            <AdminTeacherProfilePage />
          </Suspense>
        } />
        <Route path="classes" element={
          <Suspense fallback={<PageLoader />}>
            <AdminClassesPage />
          </Suspense>
        } />
        {/* Legacy /admin/schedule-slots → redirect към новия nested път. */}
        <Route path="schedule-slots" element={<Navigate to="/admin/schedule/slots" replace />} />
        <Route path="rooms" element={
          <Suspense fallback={<PageLoader />}>
            <AdminRoomsPage />
          </Suspense>
        } />
        <Route path="capacity-dashboard" element={
          <Suspense fallback={<PageLoader />}>
            <AdminCapacityDashboardPage />
          </Suspense>
        } />
        <Route path="holidays" element={
          <Suspense fallback={<PageLoader />}>
            <AdminHolidaysPage />
          </Suspense>
        } />
        <Route path="categories" element={
          <Suspense fallback={<PageLoader />}>
            <AdminCategoriesPage />
          </Suspense>
        } />
        <Route path="data-integrity" element={
          <Suspense fallback={<PageLoader />}>
            <AdminDataIntegrityPage />
          </Suspense>
        } />
        {/* Phase 5.5 — Schedule View umbrella с dashboard + 4 tabs.
            На staging достъпен за QA; production promote изчаква Phase 5A. */}
        {/* Phase 5B — Rearrange preview is a sibling route (full-screen, no tabs). */}
        <Route path="schedule/rearrange/:jobId" element={
          <Suspense fallback={<PageLoader />}>
            <AdminScheduleRearrangePage />
          </Suspense>
        } />
        <Route path="schedule" element={
          <Suspense fallback={<PageLoader />}>
            <AdminSchedulePage />
          </Suspense>
        }>
          <Route index element={
            <Suspense fallback={<PageLoader />}>
              <AdminScheduleDashboardPage />
            </Suspense>
          } />
          <Route path="slots" element={
            <Suspense fallback={<PageLoader />}>
              <AdminScheduleSlotsPage />
            </Suspense>
          } />
          <Route path="rooms" element={
            <Suspense fallback={<PageLoader />}>
              <AdminScheduleRoomsPage />
            </Suspense>
          } />
          <Route path="teachers" element={
            <Suspense fallback={<PageLoader />}>
              <AdminScheduleTeachersPage />
            </Suspense>
          } />
          <Route path="classes" element={
            <Suspense fallback={<PageLoader />}>
              <AdminScheduleClassesPage />
            </Suspense>
          } />
        </Route>
        <Route path="admin-management" element={
          <Suspense fallback={<PageLoader />}>
            <AdminManagementPage />
          </Suspense>
        } />
        <Route path="system-settings" element={
          <Suspense fallback={<PageLoader />}>
            <SystemSettingsPage />
          </Suspense>
        } />
        <Route path="gdpr-audit" element={
          <Suspense fallback={<PageLoader />}>
            <GdprAuditLogPage />
          </Suspense>
        } />
        <Route path="emergency-access-log" element={
          <Suspense fallback={<PageLoader />}>
            <EmergencyAccessLogPage />
          </Suspense>
        } />
        <Route path="faq" element={
          <Suspense fallback={<PageLoader />}>
            <FaqPage />
          </Suspense>
        } />
      </Route>

      {/* Teacher Routes */}
      <Route
        path="/teacher"
        element={
          <TeacherRoute>
            <BrandingProvider>
              <Suspense fallback={<PageLoader />}>
                <TeacherLayout />
              </Suspense>
            </BrandingProvider>
          </TeacherRoute>
        }
      >
        <Route index element={
          <Suspense fallback={<PageLoader />}>
            <TeacherDashboardPage />
          </Suspense>
        } />
        <Route path="class/:classId" element={
          <Suspense fallback={<PageLoader />}>
            <ClassViewPage />
          </Suspense>
        } />
        <Route path="emergency" element={
          <Suspense fallback={<PageLoader />}>
            <EmergencyAccessPage />
          </Suspense>
        } />
        <Route path="expenses" element={
          <Suspense fallback={<PageLoader />}>
            <TeacherExpensesPage />
          </Suspense>
        } />
        <Route path="expenses/submit" element={
          <Suspense fallback={<PageLoader />}>
            <SubmitExpensePage />
          </Suspense>
        } />
        <Route path="advance" element={
          <Suspense fallback={<PageLoader />}>
            <TeacherAdvanceAccountabilityPage />
          </Suspense>
        } />
        <Route path="declare-hours" element={
          <Suspense fallback={<PageLoader />}>
            <TeacherDeclareHoursPage />
          </Suspense>
        } />
        <Route path="my-hours" element={
          <Suspense fallback={<PageLoader />}>
            <TeacherMyHoursPage />
          </Suspense>
        } />
        <Route path="salary" element={
          <Suspense fallback={<PageLoader />}>
            <TeacherSalaryPage />
          </Suspense>
        } />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <>
      <PWAUpdatePrompt />
      <RefreshResultToast />
      <ErrorBoundary>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </ErrorBoundary>
    </>
  );
}

export default App;
